import { useEffect, useState } from 'react'
import { X, Gift, Clock, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { getGamify, type GamifyState } from '@/api/gamify'
import { getRewardsCatalog, getRedemptions, redeemReward, type RewardItem, type Redemption } from '@/api/rewards'

export function RewardsPage({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [state, setState] = useState<GamifyState | null>(null)
  const [catalog, setCatalog] = useState<RewardItem[]>([])
  const [rate, setRate] = useState('')
  const [history, setHistory] = useState<Redemption[]>([])
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const refresh = () => {
    getGamify(userId).then(setState)
    getRedemptions(userId).then(setHistory)
  }
  useEffect(() => {
    getRewardsCatalog().then((d) => { setCatalog(d.catalog); setRate(d.rate) })
    refresh()
  }, [userId])

  const redeem = async (item: RewardItem) => {
    setBusy(item.id)
    const res = await redeemReward(userId, item.id)
    setMsg(res.ok ? `Requested — your ${item.label} is on its way.` : res.error || 'Could not redeem.')
    setBusy(null)
    if (res.ok) refresh()
    setTimeout(() => setMsg(null), 4000)
  }

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-background">
      <header className="flex items-center gap-2 h-16 px-4 border-b border-border shrink-0 bg-card">
        <Gift className="size-5 text-ornament-plum" />
        <div className="flex-1 leading-tight">
          <h1 className="text-base font-semibold text-foreground">Rewards</h1>
          <p className="text-[11px] text-muted-foreground">{rate || 'Redeem Health Coins for real rewards'}</p>
        </div>
        <button onClick={onClose} className="rounded-md p-1.5 hover:bg-muted" aria-label="Close"><X className="size-5 text-foreground" /></button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-lg px-4 py-5 space-y-5">
          {/* Balance */}
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Your balance</div>
              <div className="text-2xl font-bold text-foreground">{(state?.coins ?? 0).toLocaleString()} <span className="text-sm font-normal text-muted-foreground">coins</span></div>
            </div>
            <span className="rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground">{state?.level.name ?? '—'}</span>
          </div>

          {msg && <div className="rounded-lg border border-border-subtle bg-success-light px-3 py-2 text-sm text-success-text">{msg}</div>}

          {/* Catalog */}
          <div>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Redeem</h2>
            <div className="space-y-2">
              {catalog.map((item) => {
                const canAfford = (state?.coins ?? 0) >= item.coin_cost
                return (
                  <div key={item.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground">{item.label}</div>
                      <div className="text-[11px] text-muted-foreground">{item.coin_cost.toLocaleString()} coins</div>
                    </div>
                    <Button
                      size="sm" variant={canAfford ? 'primary' : 'outline'} className="shrink-0"
                      disabled={!canAfford || busy === item.id}
                      onClick={() => redeem(item)}
                    >
                      {busy === item.id ? 'Redeeming…' : canAfford ? 'Redeem' : 'Not enough coins'}
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>

          {/* History */}
          {history.length > 0 && (
            <div>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your redemptions</h2>
              <div className="space-y-2">
                {history.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 rounded-lg border border-border-subtle bg-card px-3 py-2.5">
                    {r.status === 'fulfilled'
                      ? <CheckCircle2 className="size-4 shrink-0 text-success-text" />
                      : <Clock className="size-4 shrink-0 text-muted-foreground" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-foreground truncate">{r.label}</div>
                      <div className="text-[11px] text-muted-foreground">{r.coin_cost.toLocaleString()} coins</div>
                    </div>
                    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium capitalize',
                      r.status === 'fulfilled' ? 'bg-success-light text-success-text' : 'bg-warning-light text-warning-text')}>
                      {r.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-center text-[11px] text-muted-foreground">Redemptions are processed within a few business days.</p>
        </div>
      </div>
    </div>
  )
}
